/*
  ORJN — COMPLETE THE FIT. Cross-sell row on the shoe PDP.

  THE RULE (agreed with the owner, deliberately simple):
    1. SPORT   the shoe's type picks the clothing sport. Tennis has no clothing counterpart, so it uses
               Training. Clothing tagged Lifestyle is accepted as a second-best sport for any shoe.
    2. COLOUR  exact match on custom.color, else Black / White / Grey. NOTHING ELSE. A red shoe can never
               be shown an orange hoodie: anything that is neither the shoe's colour nor a neutral is
               dropped, so the fallback cannot produce a clash. Shoes and clothing share the same closed
               13-value colour vocabulary (verified 2026-09-08), so there is nothing to translate.
    3. STOCK + GENDER  in stock only. Men / Women / "Men, Women" as everywhere else on the store.
               Kids shoes get no row — kids clothing carries age_range, not gender, and there is none yet.
    4. ONE PER SLOT  Top, Bottom, Accessory, Carry — so it reads as an outfit, not eight random products.
               Shoe accessories (laces, insoles) are excluded at the owner's request; Carry is bags and
               bottles.

  RANKING inside a slot: colour beats sport. An exact-colour Lifestyle hoodie outranks a neutral
  in-sport jersey, because matching colour is the point of the feature. Ties are broken by a hash of the
  shoe handle, so the same shoe always shows the same fit (stable on reload) while different shoes
  spread across the stock instead of every PDP showing the same hoodie — clothing_sport is a list, so
  one piece can qualify for thousands of shoes.

  A fit needs at least `min` pieces (default 2); below that the row stays hidden. The row is also hidden
  on any error, empty feed or missing template — it must never be able to break a product page.

  pickFit is a pure function exposed on window.ORJNFit so it can be unit-tested in Node (T-fit-test.mjs).
*/
(function () {
  'use strict';
  var root = typeof window !== 'undefined' ? window : globalThis;
  if (root.ORJNFit && root.ORJNFit.__booted) return;

  var SPORT_MAP = [
    ['basketball', 'Basketball'],
    ['running', 'Running'],
    ['football', 'Football'],
    ['training', 'Training'],
    ['lifestyle', 'Lifestyle'],
    ['tennis', 'Training'],
  ];
  var NEUTRALS = ['black', 'white', 'grey'];
  var SLOTS = [
    { key: 'top', label: 'Top', types: ['T-Shirts', 'Jerseys', 'Hoodies & Sweatshirts', 'Tanks & Vests', 'Shirts & Tops', 'Jackets', 'Sports Bras'] },
    { key: 'bottom', label: 'Bottom', types: ['Shorts', 'Pants & Tights', 'Skirts & Dresses'] },
    { key: 'accessory', label: 'Accessory', types: ['Hats', 'Gloves', 'Headbands & Wristbands'] },
    { key: 'carry', label: 'Carry', types: ['Bags & Wallets', 'Water Bottles & Hydration'] },
  ];
  // Offered only against a women's shoe. On a unisex shoe the viewer may be either, and a sports bra as
  // "the top" of a unisex fit reads wrong.
  var WOMEN_ONLY = ['Sports Bras', 'Skirts & Dresses'];

  function norm(s) { return String(s == null ? '' : s).trim().toLowerCase(); }
  function firstColor(c) {
    if (Array.isArray(c)) c = c[0];
    // tolerate a list metafield printed as ["Red"] into a data attribute
    return norm(String(c == null ? '' : c).replace(/[\[\]"]/g, '').split(',')[0]);
  }
  function genders(g) {
    var s = norm(Array.isArray(g) ? g.join(',') : g);
    return { men: /(^|,\s*)men(\s*,|$)/.test(s), women: /(^|,\s*)women(\s*,|$)/.test(s) };
  }
  function sportList(p) {
    var s = p && p.sports;
    if (!s) return [];
    if (!Array.isArray(s)) s = String(s).split(',');
    return s.map(norm);
  }
  function hash(str) {
    var h = 2166136261;
    for (var i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
    return h >>> 0;
  }

  function describeShoe(ds) {
    ds = ds || {};
    var g = genders(ds.gender);
    var raw = norm(ds.sport), sport = null;
    for (var i = 0; i < SPORT_MAP.length; i++) if (raw.indexOf(SPORT_MAP[i][0]) >= 0) { sport = SPORT_MAP[i][1]; break; }
    return {
      handle: String(ds.handle || ''),
      sport: sport,
      color: firstColor(ds.color),
      men: g.men && !g.women,
      women: g.women && !g.men,
      unisex: g.men && g.women,
      kids: !!(ds.kids && String(ds.kids).trim()),
    };
  }

  function genderOk(itemGender, shoe) {
    var i = genders(itemGender);
    if (!i.men && !i.women) return false;
    if (shoe.unisex) return true;
    if (shoe.men) return i.men;
    if (shoe.women) return i.women;
    return false;
  }

  function pickFit(shoeData, pool) {
    var shoe = describeShoe(shoeData);
    if (!shoe.sport || shoe.kids || !(shoe.men || shoe.women || shoe.unisex)) return [];
    var target = norm(shoe.sport);
    var picks = [];
    var used = {};
    pool = pool || [];

    SLOTS.forEach(function (slot) {
      var bestRank = Infinity, tied = [];
      for (var k = 0; k < pool.length; k++) {
        var p = pool[k];
        if (!p || p.available !== true || used[p.handle]) continue;
        var type = String(p.type == null ? '' : p.type).trim();
        if (slot.types.indexOf(type) < 0) continue;
        if (WOMEN_ONLY.indexOf(type) >= 0 && !shoe.women) continue;
        if (!genderOk(p.gender, shoe)) continue;

        var sp = sportList(p);
        var sportTier = sp.indexOf(target) >= 0 ? 0 : (sp.indexOf('lifestyle') >= 0 ? 1 : -1);
        if (sportTier < 0) continue;

        var c = firstColor(p.color);
        var colorTier = c && c === shoe.color ? 0 : (NEUTRALS.indexOf(c) >= 0 ? 1 : -1);
        if (colorTier < 0) continue; // neither the shoe's colour nor a neutral: never shown

        var rank = colorTier * 2 + sportTier; // colour dominates sport
        if (rank < bestRank) { bestRank = rank; tied = [p]; }
        else if (rank === bestRank) tied.push(p);
      }
      if (!tied.length) return;
      tied.sort(function (a, b) { return a.handle < b.handle ? -1 : a.handle > b.handle ? 1 : 0; });
      var chosen = tied[hash(shoe.handle + '|' + slot.key) % tied.length];
      used[chosen.handle] = true;
      picks.push({
        slot: slot.key,
        label: slot.label,
        product: chosen,
        colour: bestRank < 2 ? 'match' : 'neutral',
        sport: bestRank % 2 === 0 ? 'sport' : 'lifestyle',
      });
    });
    return picks;
  }

  function formatMoney(cents, fmt) {
    var n = Number(cents) || 0;
    fmt = fmt || '${{amount}}';
    function delim(prec, thou, dec) {
      var s = (n / 100).toFixed(prec).split('.');
      s[0] = s[0].replace(/\B(?=(\d{3})+(?!\d))/g, thou);
      return s[1] ? s[0] + dec + s[1] : s[0];
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      if (key === 'amount_no_decimals') return delim(0, ',', '.');
      if (key === 'amount_with_comma_separator') return delim(2, '.', ',');
      if (key === 'amount_no_decimals_with_comma_separator') return delim(0, '.', ',');
      return delim(2, ',', '.');
    });
  }

  var api = { pickFit: pickFit, describeShoe: describeShoe, formatMoney: formatMoney, SLOTS: SLOTS, NEUTRALS: NEUTRALS };
  root.ORJNFit = api;

  // ---------------------------------------------------------------- browser only below this line
  if (typeof document === 'undefined') return;
  api.__booted = true;

  var CACHE_KEY = 'orjn-fit-pool-v1';
  var TTL = 30 * 60 * 1000;
  var MAX_PAGES = 12;
  var poolPromise = null;

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function title(s) { s = String(s || ''); return s.charAt(0).toUpperCase() + s.slice(1); }

  function loadDept(handle) {
    var all = [];
    function page(n) {
      return fetch('/collections/' + handle + '?view=orjn-fit-data&page=' + n, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.text(); })
        .then(function (t) {
          var j = JSON.parse(t); // a missing template returns HTML and throws here -> treated as empty
          all = all.concat(j.products || []);
          return j.pages > n && n < MAX_PAGES ? page(n + 1) : all;
        });
    }
    return page(1).catch(function () { return all; });
  }

  function loadPool() {
    if (poolPromise) return poolPromise;
    try {
      var cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (cached && Date.now() - cached.t < TTL && Array.isArray(cached.p)) return (poolPromise = Promise.resolve(cached.p));
    } catch (e) { /* storage blocked or corrupt: fall through to the network */ }
    poolPromise = Promise.all([loadDept('clothing'), loadDept('accessories')]).then(function (r) {
      var pool = r[0].concat(r[1]);
      // Never cache an empty pool: right after the import it would hide the row for the rest of the TTL.
      if (pool.length) { try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), p: pool })); } catch (e) { /* quota */ } }
      return pool;
    });
    return poolPromise;
  }

  function card(pick, money) {
    var p = pick.product;
    var img = p.image
      ? '<img src="' + esc(p.image) + '" alt="' + esc(p.title) + '" loading="lazy" width="600" height="600">'
      : '<span class="orjn-fit__placeholder" aria-hidden="true"></span>';
    var price = '<span>' + esc(formatMoney(p.price, money)) + '</span>';
    if (Number(p.compare) > Number(p.price)) price += '<s>' + esc(formatMoney(p.compare, money)) + '</s>';
    return '<a class="orjn-fit__card" href="' + esc(p.url) + '" data-fit-slot="' + esc(pick.slot) + '" data-fit-colour="' + esc(pick.colour) + '">' +
      '<div class="orjn-fit__media">' + img + '</div>' +
      '<div class="orjn-fit__meta">' +
        '<span class="orjn-fit__slot">' + esc(pick.label) + '</span>' +
        '<h3 class="orjn-fit__name">' + esc(p.title) + '</h3>' +
        '<div class="orjn-fit__price">' + price + '</div>' +
      '</div></a>';
  }

  function mount(el) {
    if (!el || el.__orjnFit) return;
    el.__orjnFit = true;
    var ds = el.dataset;
    loadPool().then(function (pool) {
      var picks = pickFit(ds, pool);
      var min = parseInt(ds.min, 10) || 2;
      el.setAttribute('data-orjn-fit-picks', String(picks.length));
      if (picks.length < min) return; // a single piece is not a fit: stay hidden
      var grid = el.querySelector('[data-orjn-fit-grid]');
      var tag = el.querySelector('[data-orjn-fit-tag]');
      var shoe = describeShoe(ds);
      if (tag) tag.textContent = (shoe.sport || '') + (shoe.color ? ' / ' + title(shoe.color) : '');
      if (grid) grid.innerHTML = picks.map(function (pk) { return card(pk, ds.money); }).join('');
      el.hidden = false;
    }).catch(function () { /* never break the PDP */ });
  }

  function boot() {
    var wraps = document.querySelectorAll('[data-orjn-fit-wrap]');
    if (!wraps.length) return;
    var go = function (wrap) { mount(wrap.querySelector('[data-orjn-fit]')); };
    if (!('IntersectionObserver' in window)) { Array.prototype.forEach.call(wraps, go); return; }
    // Observe a 1px sentinel, not the row: the row starts hidden (display:none) and a hidden element
    // never intersects, so observing it directly would mean it never loads.
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        io.unobserve(e.target);
        go(e.target.closest('[data-orjn-fit-wrap]'));
      });
    }, { rootMargin: '700px 0px' });
    Array.prototype.forEach.call(wraps, function (w) {
      var s = w.querySelector('[data-orjn-fit-sentinel]');
      if (s) io.observe(s); else go(w);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
